import { useMemo, useState } from 'react';
import { useStore } from '../state/store';
import { TopBar } from '../components/TopBar';
import { NetWorthChart, type ChartEvent } from '../components/NetWorthChart';
import { YearRhythmStrip } from '../components/YearRhythmStrip';
import { EVENT_META } from '../events/meta';
import { BucketChart } from '../components/BucketChart';
import { ScenarioCompareTable } from '../components/ScenarioCompareTable';
import { LoansStrip } from '../components/LoansStrip';
import { SegmentedControl, ProblemBadge, ProblemPanel } from '../ui';
import { fmtMoney, fmtCompact } from '../utils/format';
import {
  getProjectionProblems,
  getYearProblemMap,
  historicalSeries,
} from '../engine/projection';

interface Props {
  onGoTo: (tab: string) => void;
}

export function ProjectionPage({ onGoTo }: Props) {
  const { projection, baselineProjection, compareProjections, activeScenario, assumptions, snapshots, accounts } = useStore();
  const hist = useMemo(() => historicalSeries(snapshots, accounts), [snapshots, accounts]);
  const [view, setView] = useState<'networth' | 'buckets' | 'mortgage'>('networth');

  // Slice 6: surface engine-detected "things to know" from the projection.
  // `problems` is severity-sorted (critical → warning → note); we cap at 5
  // for the panel list so the dashboard stays readable. `yearProblemMap`
  // gives us per-row badge rendering for the year-by-year table.
  const problems = useMemo(
    () => getProjectionProblems(projection.years, { limit: 5 }),
    [projection.years],
  );
  const yearProblemMap = useMemo(
    () => getYearProblemMap(projection.years),
    [projection.years],
  );

  const chartEvents = useMemo<ChartEvent[]>(
    () =>
      activeScenario.events
        .filter(e => e.enabled !== false)
        .map(e => {
          const meta = EVENT_META[e.kind];
          const name = ('name' in e && e.name) || meta.label;
          return {
            yearOffset: e.yearOffset,
            calendarYear: assumptions.startingCalendarYear + e.yearOffset,
            name,
            kind: e.kind,
            tone: meta.tone,
          };
        }),
    [activeScenario, assumptions.startingCalendarYear],
  );

  return (
    <>
      <TopBar
        title="Plan"
        subtitle={`${activeScenario.name} · ${projection.years.length} yrs · real $`}
        onManageScenarios={() => onGoTo('scenarios')}
      />
      <main className="mx-auto max-w-7xl space-y-4 px-4 py-4 pb-nav lg:px-6">
        <SegmentedControl
          value={view}
          onChange={setView}
          options={[
            { value: 'networth', label: 'Net Worth' },
            { value: 'buckets', label: 'Accounts' },
            { value: 'mortgage', label: 'Mortgage' },
          ]}
        />

        {view === 'networth' && (
          <>
            <section className="card card-dark p-3">
              <NetWorthChart
                years={projection.years}
                baselineYears={baselineProjection.years}
                compareSeries={compareProjections.map(c => ({
                  id: c.scenario.id,
                  label: c.scenario.name,
                  color: c.scenario.color,
                  years: c.projection.years,
                }))}
                targetRetirementAge={assumptions.targetRetirementAge}
                requiredNestEgg={projection.requiredNestEgg}
                actuals={hist}
                startingCalendarYear={assumptions.startingCalendarYear}
                currentAge={assumptions.currentAge}
                chartEvents={chartEvents}
                compensation={assumptions.compensation}
                contributionRules={assumptions.contributionRules}
              />
            </section>
            {problems.length > 0 && (
              <section className="card card-dark p-3">
                <div className="mb-2 flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold">Things to know about this plan</h2>
                  <span className="text-[11px] text-ink-500 dark:text-ink-300">
                    {problems.length} {problems.length === 1 ? 'item' : 'items'}
                  </span>
                </div>
                <div className="space-y-2">
                  {problems.map(p => (
                    <ProblemPanel
                      key={p.id}
                      severity={p.severity}
                      title={p.title}
                      body={p.body}
                      actions={p.actions}
                      termRefs={p.termRefs}
                    />
                  ))}
                </div>
              </section>
            )}
            <LoansStrip
              years={projection.years}
              mortgage={projection.mortgage}
              startingCalendarYear={assumptions.startingCalendarYear}
              totalYears={projection.years.length}
            />
            <ScenarioCompareTable />
          </>
        )}

        {view === 'buckets' && (
          <section className="card card-dark p-3">
            <BucketChart
              years={projection.years}
              startingCalendarYear={assumptions.startingCalendarYear}
              currentAge={assumptions.currentAge}
              retirementAge={assumptions.targetRetirementAge}
            />
          </section>
        )}

        {view === 'mortgage' && (
          <section className="card card-dark overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-ink-50 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-500 dark:bg-ink-800 dark:text-ink-300">
                  <th className="px-3 py-2">Year</th>
                  <th className="px-3 py-2 text-right">Beg Bal</th>
                  <th className="px-3 py-2 text-right">Interest</th>
                  <th className="px-3 py-2 text-right">Principal</th>
                  <th className="px-3 py-2 text-right">End Bal</th>
                </tr>
              </thead>
              <tbody>
                {projection.mortgage.slice(0, 12).map(m => (
                  <tr
                    key={m.yearOffset}
                    className={`border-t border-ink-100 dark:border-ink-800 ${
                      m.endBalance <= 0 ? 'bg-pos-50/40 dark:bg-pos-700/10' : ''
                    }`}
                  >
                    <td className="px-3 py-2 font-mono tabular-nums">{m.calendarYear}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(m.begBalance)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-neg-700 dark:text-neg-500">
                      {fmtMoney(m.interestPaid)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(m.principalPaid)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(m.endBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Year-rhythm strip — sub-annual cash flow rhythm for the selected
            year. Sits above the year-by-year table so the table's annual
            numbers are read in context of the within-year cadence. */}
        <YearRhythmStrip
          years={projection.years}
          assumptions={assumptions}
          onGoTo={onGoTo}
        />

        {/* Year-by-year table */}
        <section className="card card-dark overflow-hidden">
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
            Year-by-year
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-t border-ink-100 bg-ink-50 text-left text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:border-ink-800 dark:bg-ink-800 dark:text-ink-300">
                  <th className="px-3 py-2">Yr</th>
                  <th className="px-3 py-2">Age</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-1 py-2" aria-label="Notes" />

                  <th className="hidden md:table-cell px-3 py-2 text-right">HYSA</th>
                  <th className="hidden md:table-cell px-3 py-2 text-right">401(k)</th>
                  <th className="hidden md:table-cell px-3 py-2 text-right">Taxable</th>
                  <th className="hidden lg:table-cell px-3 py-2 text-right">Home</th>
                  <th className="hidden lg:table-cell px-3 py-2 text-right">Mortgage</th>
                  <th className="px-3 py-2 text-right">Purchase</th>
                  <th className="hidden md:table-cell px-3 py-2 text-right">Tax</th>
                  <th className="hidden xl:table-cell px-3 py-2 text-right">Marg.</th>
                  <th className="px-3 py-2 text-right">Investable</th>
                  <th className="px-3 py-2 text-right">NW</th>
                  <th className="px-3 py-2 text-right">SWR $</th>
                </tr>
              </thead>
              <tbody>
                {projection.years.map(y => {
                  const isRetireYr = y.age === assumptions.targetRetirementAge;
                  const yp = yearProblemMap.get(y.yearOffset);
                  return (
                    <tr
                      key={y.yearOffset}
                      className={`border-t border-ink-100 dark:border-ink-800 ${
                        isRetireYr ? 'bg-accent-50 dark:bg-accent-700/20' : ''
                      }`}
                    >
                      <td className="px-3 py-1.5 font-mono tabular-nums">{y.calendarYear}</td>
                      <td className="px-3 py-1.5 font-mono tabular-nums">{y.age}</td>
                      <td className="px-3 py-1.5">
                        <span
                          className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                            y.status === 'Working'
                              ? 'bg-accent-50 text-accent-700 dark:bg-ink-700 dark:text-accent-500'
                              : 'bg-pos-50 text-pos-700 dark:bg-ink-700 dark:text-pos-500'
                          }`}
                        >
                          {y.status}
                        </span>
                      </td>
                      <td className="px-1 py-1.5">
                        {yp && yp.severity ? (
                          <ProblemBadge severity={yp.severity} tooltip={yp.tooltip} />
                        ) : null}
                      </td>
                      <td className="hidden md:table-cell px-3 py-1.5 text-right font-mono tabular-nums text-ink-600 dark:text-ink-200">
                        {fmtCompact(y.hysa.end)}
                      </td>
                      <td className="hidden md:table-cell px-3 py-1.5 text-right font-mono tabular-nums text-ink-600 dark:text-ink-200">
                        {fmtCompact(y.retirement.end)}
                      </td>
                      <td className="hidden md:table-cell px-3 py-1.5 text-right font-mono tabular-nums text-ink-600 dark:text-ink-200">
                        {fmtCompact(y.taxable.end)}
                      </td>
                      <td className="hidden lg:table-cell px-3 py-1.5 text-right font-mono tabular-nums text-ink-600 dark:text-ink-200">
                        {fmtCompact(y.homeValue)}
                      </td>
                      <td className="hidden lg:table-cell px-3 py-1.5 text-right font-mono tabular-nums text-neg-700 dark:text-neg-500">
                        {y.mortgageBalance > 0 ? fmtCompact(y.mortgageBalance) : '—'}
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right font-mono tabular-nums ${
                          (y.purchasePlanned ?? 0) > 0
                            ? 'text-neg-700 dark:text-neg-500 font-semibold'
                            : 'text-ink-300 dark:text-ink-500'
                        }`}
                        data-col="purchase"
                      >
                        {(y.purchasePlanned ?? 0) > 0
                          ? `−${fmtCompact(y.purchasePlanned ?? 0)}`
                          : '—'}
                      </td>
                      <td
                        className={`hidden md:table-cell px-3 py-1.5 text-right font-mono tabular-nums ${
                          y.tax && y.tax.totalTax > 0
                            ? 'text-neg-700 dark:text-neg-500'
                            : 'text-ink-400'
                        }`}
                        title={
                          y.tax
                            ? `Fed ${fmtCompact(y.tax.federalOrdinaryTax + y.tax.federalLTCGTax)} · FICA ${fmtCompact(y.tax.ficaTotal)} · State ${fmtCompact(y.tax.stateTax)}${y.tax.irmaaSurcharge > 0 ? ` · IRMAA ${fmtCompact(y.tax.irmaaSurcharge)}` : ''}`
                            : undefined
                        }
                      >
                        {y.tax && y.tax.totalTax > 0 ? `−${fmtCompact(y.tax.totalTax)}` : '—'}
                      </td>
                      <td className="hidden xl:table-cell px-3 py-1.5 text-right font-mono tabular-nums text-ink-500 dark:text-ink-300">
                        {y.tax ? `${Math.round(y.tax.marginalOrdinaryRate * 100)}%` : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{fmtCompact(y.investable)}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{fmtCompact(y.netWorth)}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{fmtCompact(y.swrIncome)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </>
  );
}

